import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { contactInfo, principles, voices } from '@/mocks/site';

export default function About() {
  usePageMeta({
    title: '关于我们｜黑料雷达的内容原则与定位 - 黑料雷达',
    description:
      '黑料雷达是一个聚焦网站安全事件与隐私风险的中文情报站。了解我们的网站定位、发展理念、内容原则、更正机制以及读者口碑与联系方式。',
    keywords: '关于黑料雷达,内容原则,网站定位,安全情报站',
    canonical: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于黑料雷达',
        url: 'https://worswgu.cn/about',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '黑料雷达',
        url: 'https://worswgu.cn/',
        email: contactInfo.email,
        description: contactInfo.tagline,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://worswgu.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://worswgu.cn/about' },
        ],
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于黑料雷达
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600">
            {contactInfo.tagline}。我们做的不只是「曝光」，而是把公开信息整理成普通人能看懂、能执行的风险提示。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们是谁</h2>
            <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-700">
              <p>
                黑料雷达是一个独立运营的中文信息整理站点，关注的对象很具体：那些你在注册、授权、下单之前
                无法一眼看穿的网站安全问题。数据泄露、接口越权、免密代扣陷阱、超范围索取权限、违规共享用户画像，
                以及来自监管的公开通报，都在我们的整理范围内。
              </p>
              <p>
                我们不生产情绪，只做三件事：把散落在各处的信息聚合起来、把专业描述翻译成日常语言、
                并给出遇到同类情况时可以马上执行的动作。相比耸动的标题，我们更在意你读完以后是否真的知道下一步该做什么。
              </p>
              <p>
                站内所有事件均以公开信息为基础整理，涉及具体平台时以描述问题类型为主，不代替司法或监管作最终定性。
                如果你是被提及的一方，并持有可以推翻原始结论的证据，欢迎联系我们更正。
              </p>
            </div>

            <h2 className="mt-12 font-heading text-xl font-bold text-foreground-950 md:text-2xl">内容原则</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{item.description}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              我们不做的事
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-close-line text-base"></i>
                </span>
                不提供任何付费删稿服务，也不会以曝光为由索取利益。
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-close-line text-base"></i>
                </span>
                不使用未经核实的匿名指控作为结论，不披露报料人身份信息。
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-close-line text-base"></i>
                </span>
                不替代用户做「这个平台绝对安全」或「绝对不安全」的最终判断。
              </li>
            </ul>
          </div>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">网站定位一览</h2>
              <dl className="mt-4 space-y-4 text-sm">
                <div>
                  <dt className="text-xs text-foreground-500">面向人群</dt>
                  <dd className="mt-1 text-foreground-800">普通上网用户、小微企业主、隐私与合规从业者</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">内容类型</dt>
                  <dd className="mt-1 text-foreground-800">事件整理、风险解读、自保建议、监管通报聚合</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">更新节奏</dt>
                  <dd className="mt-1 text-foreground-800">每个工作日更新，重大事件当日跟进</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">更正机制</dt>
                  <dd className="mt-1 text-foreground-800">收到有效证据后更正，并保留更新记录</dd>
                </div>
              </dl>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className="ri-mail-send-line text-base"></i>
                  </span>
                  线索报料：{contactInfo.email}
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  更正与合作：{contactInfo.cooperativeEmail}
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className="ri-time-line text-base"></i>
                  </span>
                  {contactInfo.workingHours}
                </li>
              </ul>
              <Link
                to="/contact"
                className="mt-5 block whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-center text-sm text-background-50 transition-colors hover:bg-primary-700"
              >
                前往报料页面
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">读者怎么说</h2>
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((voice) => (
              <blockquote
                key={voice.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-6 w-6 items-center justify-center text-accent-500">
                  <i className="ri-double-quotes-l text-xl"></i>
                </span>
                <p className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{voice.quote}</p>
                <footer className="mt-4 border-t border-background-100 pt-3 text-xs text-foreground-500">
                  <span className="font-medium text-foreground-800">{voice.name}</span> · {voice.role}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      </section>
    </>
  );
}